import rootPackage from '../../../../package.json';
import { parseSettings, SETTINGS_KEY } from '../app/settings.ts';
import { browserStore, type KeyValueStore } from '../storage/kv.ts';

/**
 * Shared settings for everything the web app sends to the api besides fetching
 * puzzles: the device id, analytics events and solve sync.
 */

/** The repo version (one version spans the monorepo), sent as `appVersion` / `clientVersion`. */
export const APP_VERSION: string = String((rootPackage as { version?: unknown }).version ?? 'unknown').slice(0, 40);

/** The one localStorage-backed store the platform uses (every access guarded). */
export const platformStore: KeyValueStore = browserStore();

export const platformKeys = {
	device: 'arrow:v1:device',
	firstSeen: 'arrow:v1:first-seen',
	events: 'arrow:v1:events',
	pendingSolves: 'arrow:v1:solves:pending',
	/** Dev toggle: '1' turns analytics and solve sync off on this device. */
	noAnalytics: 'arrow:v1:dev:noanalytics',
	/** '1': an internal device (James's, a tester's). Still sends, flagged, and the reports leave it out. */
	internal: 'arrow:v1:internal',
} as const;

/**
 * Applies the URL flags, each remembered on this device:
 * - `?noanalytics` (or `=1`) turns analytics and solve sync off; `?noanalytics=0` back on.
 * - `?internal=1` (or bare `?internal`) marks this as an internal device: events and the
 *   registration carry `internal: true`, the api marks the player, and the analytics
 *   leave it out (docs/ANALYTICS.md, "Test and internal devices"). `?internal=0` stops
 *   sending the flag, but the api's mark is sticky.
 * Called once on boot by `initPlatform`; safe to call again.
 */
export function applyUrlFlags(url: string | undefined = currentUrl(), store: KeyValueStore = platformStore): void {
	if (!url) return;
	try {
		const params = new URL(url).searchParams;
		const off = (value: string | null) => value === '0' || value === 'false';
		if (params.has('noanalytics')) setAnalyticsEnabled(off(params.get('noanalytics')), store);
		if (params.has('internal')) setInternalDevice(!off(params.get('internal')), store);
	} catch {
		// A url we can't parse sets nothing.
	}
}

/** Marks (or unmarks) this as an internal device: what `?internal=1` and the dev panel set. */
export function setInternalDevice(internal: boolean, store: KeyValueStore = platformStore): void {
	if (internal) store.set(platformKeys.internal, '1');
	else store.remove(platformKeys.internal);
}

/** Whether this device is marked internal (`?internal=1`). Never throws. */
export function isInternalDevice(store: KeyValueStore = platformStore): boolean {
	try {
		return store.get(platformKeys.internal) === '1';
	} catch {
		return false;
	}
}

/** The dev toggle (also what `?noanalytics` sets). */
export function setAnalyticsEnabled(enabled: boolean, store: KeyValueStore = platformStore): void {
	if (enabled) store.remove(platformKeys.noAnalytics);
	else store.set(platformKeys.noAnalytics, '1');
}

/**
 * Whether analytics events and solve sync go out. Off in unit tests (vitest), when
 * the dev toggle is set, and while the current url carries `?noanalytics`.
 */
export function isAnalyticsEnabled(store: KeyValueStore = platformStore): boolean {
	if (import.meta.env.MODE === 'test') return false;
	if (store.get(platformKeys.noAnalytics) === '1') return false;
	const url = currentUrl();
	if (url) {
		try {
			const value = new URL(url).searchParams.get('noanalytics');
			if (value !== null && value !== '0' && value !== 'false') return false;
		} catch {
			// Ignore.
		}
	}
	return true;
}

/**
 * Whether usage counts (analytics events, the device registration) go out: analytics
 * aren't switched off for dev (`isAnalyticsEnabled`) and the player hasn't turned off
 * Settings → "Count my plays" (`settings.analytics`, privacy page). Solves follow their
 * own rule (sync.ts): with counts off, only what a challenge needs is sent.
 */
export function usageCountsOn(store: KeyValueStore = platformStore): boolean {
	if (!isAnalyticsEnabled(store)) return false;
	try {
		return parseSettings(store.get(SETTINGS_KEY)).analytics;
	} catch {
		return true;
	}
}

function currentUrl(): string | undefined {
	try {
		return typeof window === 'undefined' ? undefined : window.location.href;
	} catch {
		return undefined;
	}
}

/** `navigator.sendBeacon`, guarded (it can throw, or be missing), or null where there isn't one. */
export function browserBeacon(): ((url: string, data: Blob) => boolean) | null {
	try {
		if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return null;
		return (url, data) => {
			try {
				return navigator.sendBeacon(url, data);
			} catch {
				return false;
			}
		};
	} catch {
		return null;
	}
}
